'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrencyConfig, COUNTRY_LIST, type CurrencyConfig } from '@/lib/geoLocale';

interface LocaleContextValue {
  selectedCountry: string;
  currencyConfig: CurrencyConfig;
  setCountry: (code: string) => void;
  isLoading: boolean;
}

const LocaleContext = createContext<LocaleContextValue>({
  selectedCountry: 'IN',
  currencyConfig: getCurrencyConfig('IN'),
  setCountry: () => {},
  isLoading: true,
});

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [selectedCountry, setSelectedCountry] = useState('IN');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Try to load saved preference
    const saved = localStorage.getItem('tubenexa_country');
    if (saved) {
      setSelectedCountry(saved);
      setIsLoading(false);
      return;
    }

    // Try to detect via IP (using a free geo API)
    fetch('https://ipapi.co/json/')
      .then((r) => r.json())
      .then((data) => {
        const code = data?.country_code || 'IN';
        const found = COUNTRY_LIST.find((c) => c.code === code);
        const finalCode = found ? code : 'IN';
        setSelectedCountry(finalCode);
        localStorage.setItem('tubenexa_country', finalCode);
      })
      .catch(() => {
        setSelectedCountry('IN');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const setCountry = useCallback((code: string) => {
    setSelectedCountry(code);
    localStorage.setItem('tubenexa_country', code);
  }, []);

  const currencyConfig = getCurrencyConfig(selectedCountry);

  return (
    <LocaleContext.Provider value={{ selectedCountry, currencyConfig, setCountry, isLoading }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}
